import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";

import ProtectedRoute from "./ProtectedRoute";
import RoleGuard from "./RoleGuard";

/* LAYOUTS */
import PatientLayout from "../layouts/PatientLayout";
import PhysicianLayout from "../layouts/PhysicianLayout";
import AdminLayout from "../layouts/AdminLayout";
import FacilitiesLayout from "../layouts/FacilitiesLayout";
import ViewFacility from "../pages/admin/ViewFacility";
import PublicLayout from "../layouts/PublicLayout";
import LoaderWrapper from "../components/Loader";


// Public Routes
const LandingPage = lazy(() => import("../pages/LandingPage"));
const TermsConditions = lazy(() => import("../pages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const GuestVideoSession = lazy(() => import("../pages/videoSession/GuestVideoSession"));

/* AUTH */
const Login = lazy(() => import("../pages/auth/home"));
const EmailVerification = lazy(() => import("../pages/auth/emailVerification"));
const ResetPassword = lazy(() => import("../pages/auth/resetPassword"));
const PageNotFound = lazy(() => import("../pages/pageNotFound"));

/* PATIENT */
const Chat = lazy(() => import("../pages/patient/chat"));
const UpcomingAppointments = lazy(() => import("../pages/patient/upcomingAppointments"));
const UpcomingAppointmentDetail = lazy(() => import("../pages/patient/upcomingAppointmentDetail"));
const PastAppointments = lazy(() => import("../pages/patient/pastAppointments"));
const PastAppointmentDetail = lazy(() => import("../pages/patient/pastAppointmentDetail"));
const PatientProfile = lazy(() => import("../pages/patient/PatientProfile"));
const ReviewPatientForm = lazy(() => import("../pages/patient/reviewPatientForm"));
const Drafts = lazy(() => import("../pages/patient/Drafts"));
const FeedbackForm = lazy(() => import("../pages/patient/FeedbackForm"));
const PatientChangePass = lazy(() => import("../pages/patient/PatientChangePass"));
const PatientGuid = lazy(() => import("../pages/patient/PatientGuid"));

/* PHYSICIAN */
const UpcomingPhysicianAppointments = lazy(() => import("../pages/physician/UpcomingAppointments"));
const PastPhysicianAppointments = lazy(() => import("../pages/physician/PastAppointments"));
const PatientPreview = lazy(() => import("../pages/physician/PatientPreview"));
const PhysicianProfile = lazy(() => import("../pages/physician/PhysicianProfile"));
const AppointmentView = lazy(() => import("../pages/physician/AppointmentView"));
const PhysicianSchedule = lazy(() => import("../pages/physician/PhysicianSchedule"));
const PhysicianChangePass = lazy(() => import("../pages/physician/PhysicianChangePass"));
const ReviewPatientFormPhysician = lazy(() => import("../pages/physician/reviewPatientForm"));
const PhysicianGuid = lazy(() => import("../pages/physician/PhysicianGuid"));
const PatientPreviewOld = lazy(() => import("../pages/physician/PatientPreviewOld"));
const VideoSession = lazy(() => import("../pages/physician/VideoSession"));

/* Facilities */
const Patients = lazy(() => import("../pages/facilities/Patients"));
const PatientDetail = lazy(() => import("../pages/facilities/PatientDetail"));
const FacilityProfile = lazy(() => import("../pages/facilities/FacilityProfile"));
const FacilityChangePass = lazy(() => import("../pages/facilities/FacilityChangePass"));
const FacilitiesGuid = lazy(() => import("../pages/facilities/FacilitiesGuid"));
const FacilityRequestProvider = lazy(() => import("../pages/facilities/FacilityRequestProvider"));
const CreateFacilityProvider = lazy(() => import("../pages/facilities/CreateFacilityProvider"));
const FacilityViewProvider = lazy(() => import("../pages/facilities/FacilityViewProvider"));

/* ADMIN */
const Dashboard = lazy(() => import("../pages/admin/Dashboard"));
const Users = lazy(() => import("../pages/admin/Users"));
const Physicians = lazy(() => import("../pages/admin/Physicians"));
const CreatePhysician = lazy(() => import("../pages/admin/CreatePhysician"));
const ViewPhysicians = lazy(() => import("../pages/admin/ViewPhysicians"));
const Facility = lazy(() => import("../pages/admin/Facility"));
const CreateFacility = lazy(() => import("../pages/admin/CreateFacility"));
const TestRequest = lazy(() => import("../pages/admin/TestRequest"));
const TestRequestDetail = lazy(() => import("../pages/admin/TestRequestDetail"));
const SubmittedForms = lazy(() => import("../pages/admin/SubmittedForms"));
const SubmittedFormsDetail = lazy(() => import("../pages/admin/SubmittedFormsDetail"));
const Admin = lazy(() => import("../pages/admin/Admin"));
const CreateAdmin = lazy(() => import("../pages/admin/CreateAdmin"));
const UserFeedback = lazy(() => import("../pages/admin/UserFeedback"));
const ContactDetails = lazy(() => import("../pages/admin/ContactDetails"));
const AdminProfile = lazy(() => import("../pages/admin/AdminProfile"));
const AdminChangePass = lazy(() => import("../pages/admin/AdminChangePass"));
const Newsletter = lazy(() => import("../pages/admin/Newsletter"));
const TermsCondition = lazy(() => import("../pages/admin/TermsCondition"));
const PaymentReceived = lazy(() => import("../pages/admin/Payments"));
const RequestProviderVisit = lazy(() => import("../pages/admin/RequestProviderVisit"));
const CreateProviderVisit = lazy(() => import("../pages/admin/CreateProviderVisit"));
const ViewProviderVisit = lazy(() => import("../pages/admin/ViewProviderVisit"));
const Devices = lazy(() => import("../pages/admin/Devices"));
const AdminPatients = lazy(() => import("../pages/admin/Patients"));
const CreatePatient = lazy(() => import("../pages/admin/CreatePatient"));
const ViewPatient = lazy(() => import("../pages/admin/ViewPatient"));
const MobileLabs = lazy(() => import("../pages/admin/MobileLabs"));
const CreateMobileLab = lazy(() => import("../pages/admin/CreateMobileLab"));
const ViewMobileLab = lazy(() => import("../pages/admin/ViewMobileLab"));
const ReviewPatientFormAdmin = lazy(() => import("../pages/admin/reviewPatientForm"));



function PhysicianSessionBootstrap() {
    const location = useLocation();
    const appointmentId = new URLSearchParams(location.search).get("physicianSession");

    if (appointmentId) {
        return (
            <Navigate
                replace
                to={`/physician/appointments/${encodeURIComponent(appointmentId)}/session`}
            />
        );
    }

    return <LandingPage />;
}

export default function AppRouter() {
    return (
        <Suspense fallback={<LoaderWrapper />}>

            <Routes>
                <Route path="/ld" element={<LoaderWrapper />} />

                <Route element={<PublicLayout />}>
                    <Route path="/" element={<PhysicianSessionBootstrap />} />
                    <Route path="/terms" element={<TermsConditions />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                </Route>


                <Route path="/login" element={<Login />} />
                <Route path="/verify-email" element={<EmailVerification />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/video-session/guest/:token" element={<GuestVideoSession />} />

                <Route
                    path="/physician/appointments/:appointmentId/session"
                    element={
                        <ProtectedRoute>
                            <RoleGuard allowedRoles={["6a4cbf11742c7c931709101d"]}>
                                <VideoSession />
                            </RoleGuard>
                        </ProtectedRoute>
                    }
                />

                {/* -------- AUTH -------- */}
                {/* <Route element={<AuthLayout />}>
                    
                </Route> */}

                {/* -------- PATIENT PORTAL -------- */}
                <Route
                    element={
                        <ProtectedRoute>
                            <RoleGuard allowedRoles={["6a4cbf0a742c7c931709101b"]}>
                                <PatientLayout />
                            </RoleGuard>
                        </ProtectedRoute>
                    }
                >
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/upcoming-appointment" element={<UpcomingAppointments />} />
                    <Route path="/upcoming-appointment-detail" element={<UpcomingAppointmentDetail />} />
                    <Route path="/past-appointment" element={<PastAppointments />} />
                    <Route path="/past-appointment-detail" element={<PastAppointmentDetail />} />
                    <Route path="/review-patient-form" element={<ReviewPatientForm />} />
                    <Route path="/profile" element={<PatientProfile />} />
                    <Route path="/drafts" element={<Drafts />} />
                    <Route path="/feedback" element={<FeedbackForm />} />
                    <Route path="/account" element={<PatientChangePass />} />
                    <Route path="/patient-guide" element={<PatientGuid />} />
                </Route>

                {/* -------- PHYSICIAN PORTAL -------- */}
                <Route
                    element={<>
                        <ProtectedRoute>
                            <RoleGuard allowedRoles={["6a4cbf11742c7c931709101d"]}>
                                <PhysicianLayout />
                            </RoleGuard>
                        </ProtectedRoute>
                    </>}
                >
                    <Route path="/physician/dashboard" element={<AppointmentView />} />
                    <Route path="/physician/upcoming-appointments" element={<UpcomingPhysicianAppointments />} />
                    <Route path="/physician/past-appointments" element={<PastPhysicianAppointments />} />
                    <Route path="/physician/patient-preview" element={<PatientPreview />} />
                    <Route path="/physician/schedule" element={<PhysicianSchedule />} />
                    <Route path="/physician/profile" element={<PhysicianProfile />} />
                    <Route path="/physician/account" element={<PhysicianChangePass />} />
                    <Route path="/physician/review-patient-form" element={<ReviewPatientFormPhysician />} />
                    <Route path="/physician/physician-guide" element={<PhysicianGuid />} />
                    <Route path="/physician/patient/view" element={<PatientPreviewOld />} />
                </Route>

                {/* -------- Facilities PORTAL -------- */}
                <Route
                    element={<>
                        <ProtectedRoute>
                            <RoleGuard allowedRoles={["6a4cbf18742c7c931709101f"]}>
                                <FacilitiesLayout />
                            </RoleGuard>
                        </ProtectedRoute>
                    </>}
                >
                    <Route path="/facilities/patients" element={<Patients />} />
                    <Route path="/facilities/patients/detail" element={<PatientDetail />} />
                    <Route path="/facilities/account" element={<FacilityChangePass />} />
                    <Route path="/facilities/profile" element={<FacilityProfile />} />
                    <Route path="/facilities/guide" element={<FacilitiesGuid />} />
                    <Route path="/facilities/provider-requests" element={<FacilityRequestProvider />} />
                    <Route path="/facilities/provider-requests/create" element={<CreateFacilityProvider />} />
                    <Route path="/facilities/provider-requests/view" element={<FacilityViewProvider />} />
                </Route>

                {/* -------- ADMIN PORTAL -------- */}
                <Route
                    element={<>
                        <ProtectedRoute>
                            <RoleGuard allowedRoles={["6a4cbece742c7c9317091019"]}>
                                <AdminLayout />
                            </RoleGuard>
                        </ProtectedRoute>
                    </>}
                >
                    <Route path="/admin/admin" element={<Admin />} />
                    <Route path="/admin/admin/create" element={<CreateAdmin />} />
                    <Route path="/admin/dashboard" element={<Dashboard />} />
                    <Route path="/admin/users" element={<Users />} />
                    <Route path="/admin/physicians" element={<Physicians />} />
                    <Route path="/admin/physicians/create" element={<CreatePhysician />} />
                    <Route path="/admin/physicians/view" element={<ViewPhysicians />} />
                    <Route path="/admin/facility" element={<Facility />} />
                    <Route path="/admin/facility/create" element={<CreateFacility />} />
                    <Route path="/admin/facility/view" element={<ViewFacility />} />
                    <Route path="/admin/test" element={<TestRequest />} />
                    <Route path="/admin/test/detail" element={<TestRequestDetail />} />
                    <Route path="/admin/submitted-forms" element={<SubmittedForms />} />
                    <Route path="/admin/submitted-forms/detail" element={<SubmittedFormsDetail />} />
                    <Route path="/admin/user-feedback" element={<UserFeedback />} />
                    <Route path="/admin/contacts" element={<ContactDetails />} />
                    <Route path="/admin/profile" element={<AdminProfile />} />
                    <Route path="/admin/account" element={<AdminChangePass />} />
                    <Route path="/admin/newsletter" element={<Newsletter />} />
                    <Route path="/admin/terms-conditions" element={<TermsCondition />} />
                    <Route path="/admin/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/admin/payments" element={<PaymentReceived />} />
                    <Route path="/admin/examination" element={<RequestProviderVisit />} />
                    <Route path="/admin/examination/create" element={<CreateProviderVisit />} />
                    <Route path="/admin/examination/view" element={<ViewProviderVisit />} />
                    <Route path="/admin/patient/review" element={<ReviewPatientFormAdmin />} />
                    <Route path="/admin/devices" element={<Devices />} />
                    <Route path="/admin/patients" element={<AdminPatients />} />
                    <Route path="/admin/patients/create" element={<CreatePatient />} />
                    <Route path="/admin/patients/view" element={<ViewPatient />} />
                    <Route path="/admin/mobile-labs" element={<MobileLabs />} />
                    <Route path="/admin/mobile-labs/create" element={<CreateMobileLab />} />
                    <Route path="/admin/mobile-labs/view" element={<ViewMobileLab />} />
                </Route>

                {/* -------- 404 -------- */}
                <Route path="/page-not-found" element={<PageNotFound />} />
                <Route path="*" element={<PageNotFound />} />

            </Routes>

        </Suspense>

    );
}
